import { siteConfig } from '@/config/site'

const META_ATTR = 'data-seo-meta'
const JSON_LD_ID = 'seo-json-ld'

function upsertMeta(attrName, attrValue, content, attrKey = 'name') {
  if (!content) return
  let el = document.head.querySelector(`meta[${attrKey}="${attrValue}"][${META_ATTR}]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attrKey, attrValue)
    el.setAttribute(META_ATTR, 'true')
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(rel, href) {
  if (!href) return
  let el = document.head.querySelector(`link[rel="${rel}"][${META_ATTR}]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    el.setAttribute(META_ATTR, 'true')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function removeSeoJsonLd() {
  const existing = document.getElementById(JSON_LD_ID)
  if (existing) existing.remove()
}

function setJsonLd(data) {
  removeSeoJsonLd()
  if (!data) return
  const script = document.createElement('script')
  script.id = JSON_LD_ID
  script.type = 'application/ld+json'
  script.setAttribute(META_ATTR, 'true')
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

function resolveSiteOrigin() {
  const configured = String(siteConfig.url || '').trim().replace(/\/$/, '')
  if (configured && /^https?:\/\//i.test(configured)) {
    return configured
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return configured
}

function absoluteUrl(path = '') {
  const base = resolveSiteOrigin()
  if (!path) return base
  const normalized = path.startsWith('/') ? path : `/${path}`
  return base ? `${base}${normalized}` : normalized
}

export function updateSeo(options = {}) {
  const {
    title,
    description = siteConfig.description,
    keywords = siteConfig.keywords,
    path = '/',
    image,
    type = 'website',
    robots = 'index,follow',
    jsonLd
  } = options

  const fullTitle = title
    ? `${title} - ${siteConfig.name}`
    : `${siteConfig.name} - ${siteConfig.slogan}`

  const canonical = absoluteUrl(path)
  const ogImage = image || absoluteUrl('/og/default.png')

  document.title = fullTitle

  upsertMeta('name', 'description', description)
  upsertMeta('name', 'keywords', keywords)
  upsertMeta('name', 'author', siteConfig.author)
  upsertMeta('name', 'robots', robots)
  upsertMeta('name', 'googlebot', robots)

  upsertMeta('property', 'og:title', fullTitle, 'property')
  upsertMeta('property', 'og:description', description, 'property')
  upsertMeta('property', 'og:type', type, 'property')
  upsertMeta('property', 'og:url', canonical, 'property')
  upsertMeta('property', 'og:site_name', siteConfig.name, 'property')
  upsertMeta('property', 'og:locale', siteConfig.locale, 'property')
  upsertMeta('property', 'og:image', ogImage, 'property')
  upsertMeta('property', 'og:image:secure_url', ogImage, 'property')
  upsertMeta('property', 'og:image:width', '1200', 'property')
  upsertMeta('property', 'og:image:height', '630', 'property')
  upsertMeta('property', 'og:image:alt', title || siteConfig.name, 'property')

  upsertMeta('itemprop', 'name', fullTitle, 'itemprop')
  upsertMeta('itemprop', 'description', description, 'itemprop')
  upsertMeta('itemprop', 'image', ogImage, 'itemprop')

  const twitterCard = 'summary_large_image'
  upsertMeta('name', 'twitter:card', twitterCard)
  upsertMeta('name', 'twitter:title', fullTitle)
  upsertMeta('name', 'twitter:description', description)
  upsertMeta('name', 'twitter:image', ogImage)

  upsertLink('canonical', canonical)
  upsertLink('alternate', absoluteUrl('/rss.xml'))

  setJsonLd(jsonLd)
}

export function buildWebsiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteConfig.url}/#website`,
        url: siteConfig.url,
        name: siteConfig.name,
        description: siteConfig.description,
        inLanguage: siteConfig.language,
        publisher: { '@id': `${siteConfig.url}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteConfig.url}/articles?q={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@type': 'Organization',
        '@id': `${siteConfig.url}/#organization`,
        name: siteConfig.name,
        url: siteConfig.url,
        logo: {
          '@type': 'ImageObject',
          url: `${siteConfig.url}/favicon.ico`
        }
      },
      {
        '@type': 'Blog',
        '@id': `${siteConfig.url}/#blog`,
        url: siteConfig.url,
        name: siteConfig.name,
        description: siteConfig.description,
        inLanguage: siteConfig.language,
        publisher: { '@id': `${siteConfig.url}/#organization` }
      }
    ]
  }
}

export function buildHomeJsonLd(articles = []) {
  const itemList = articles.slice(0, 10).map((article, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: absoluteUrl(`/article/${article.id}`),
    name: article.title
  }))

  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...buildWebsiteJsonLd()['@graph'],
      {
        '@type': 'ItemList',
        '@id': `${siteConfig.url}/#latest-articles`,
        name: '最新文章',
        numberOfItems: itemList.length,
        itemListElement: itemList
      }
    ]
  }
}

export function buildArticleJsonLd(article) {
  if (!article) return null

  const image = `${siteConfig.url}/og/article-${article.id}.svg`

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.summary,
    image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: article.author
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
      logo: {
        '@type': 'ImageObject',
        url: `${siteConfig.url}/favicon.ico`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absoluteUrl(`/article/${article.id}`)
    },
    keywords: article.tags.join(','),
    articleSection: article.subCategory || article.category,
    inLanguage: siteConfig.language
  }
}

export function buildBreadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path)
    }))
  }
}

export { absoluteUrl }
